import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { HandCoins, Home, GraduationCap, Stethoscope, Baby, Accessibility,
  Sprout, TreePine, LifeBuoy, ArrowRight, Heart, Users, Utensils, HandHelping } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1579863834020-2a9366e128ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxuYXR1cmUlMjBjb25zZXJ2YXRpb24lMjBpbmRpYXxlbnwwfHx8fDE3ODIzNTcxMTJ8MA&ixlib=rb-4.1.0&q=85";

const iconMap = { HandCoins, Home, GraduationCap, Stethoscope, Baby, Accessibility, Sprout, TreePine, LifeBuoy };

const areas = [
  { key: "a", Icon: "HandCoins", color: "#C95B24", bg: "#FCEAD9" },
  { key: "b", Icon: "Home", color: "#5B4023", bg: "#F0E6D2" },
  { key: "c", Icon: "GraduationCap", color: "#0F5972", bg: "#DDEFF7" },
  { key: "d", Icon: "Stethoscope", color: "#8B1E3F", bg: "#F7D9E1" },
  { key: "e", Icon: "Baby", color: "#8E3B1D", bg: "#F7E0DA" },
  { key: "f", Icon: "Accessibility", color: "#4B2E83", bg: "#E4DEF3" },
  { key: "g", Icon: "Sprout", color: "#1B4332", bg: "#DCEEDA" },
  { key: "h", Icon: "TreePine", color: "#2F6B3A", bg: "#DCEEDA" },
  { key: "i", Icon: "LifeBuoy", color: "#B8391A", bg: "#F7DCD4" },
];

const HomePage = () => {
  const { t, lang } = useApp();
  const [stats, setStats] = useState({ total_amount: 0, total_donors: 0 });
  const [volCount, setVolCount] = useState(0);

  useEffect(() => {
    api.get("/donors/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/volunteers/count").then((r) => setVolCount(r.data?.total || 0)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Nature" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1B4332]/85 via-[#1B4332]/60 to-[#C95B24]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-36 text-[#FDFBF7]">
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#F4EFE6] mb-6 border border-[#F4EFE6]/40 px-4 py-1.5 rounded-full">
              {t("heroTagline")}
            </span>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6">
              {t("heroTitle")}
            </h1>
            <p className="font-serif-display text-2xl sm:text-3xl text-[#F5B15A] italic mb-6" data-testid="brand-tagline">
              "{t("brandTagline")}"
            </p>
            <p className="text-lg md:text-xl text-[#FDFBF7]/90 max-w-2xl leading-relaxed mb-10">
              {t("heroSubtitle")}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/donate">
                <Button className="btn-saffron rounded-full px-8 py-6 text-base" data-testid="hero-donate-btn">
                  <Heart className="w-5 h-5 mr-2" /> {t("ctaDonate")}
                </Button>
              </Link>
              <Link to="/work">
                <Button variant="outline" className="rounded-full px-8 py-6 text-base bg-transparent border-[#FDFBF7]/60 text-[#FDFBF7] hover:bg-[#FDFBF7] hover:text-[#1B4332]" data-testid="hero-work-btn">
                  {t("ctaLearn")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT NUMBERS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C95B24] font-semibold mb-3">{t("impactHeading")}</p>
          <h2 className="font-serif-display text-3xl sm:text-5xl text-[#1B4332]">{lang === "hi" ? "करुणा में जीते हुए" : "Living in Compassion"}</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { Icon: Users, val: `${stats.total_donors || 0}+`, label: lang === "hi" ? "दानदाता" : "Donors" },
            { Icon: HandHelping, val: `${volCount || 0}+`, label: lang === "hi" ? "स्वयंसेवक" : "Volunteers" },
            { Icon: TreePine, val: "10,000+", label: t("treesPlanted") },
            { Icon: Utensils, val: "50,000+", label: t("animalsFed") },
          ].map((s, i) => (
            <div key={i} className={`card-tactile p-8 text-center animate-fade-up delay-${i+1}`} data-testid={`stat-card-${i}`}>
              <s.Icon className="w-9 h-9 mx-auto text-[#C95B24] mb-3" />
              <div className="font-serif-display text-4xl text-[#1B4332] mb-2">{s.val}</div>
              <div className="text-xs uppercase tracking-[0.15em] text-[#4A4A4A]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* NINE AREAS */}
      <section className="bg-[#F4EFE6] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-14 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.25em] text-[#C95B24] font-semibold mb-3">Our Work</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1B4332] mb-4">{t("ourPillars")}</h2>
            <p className="text-[#4A4A4A] text-lg">{t("ourPillarsSub")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pillars-grid">
            {areas.map((p, i) => {
              const Ico = iconMap[p.Icon];
              return (
                <div
                  key={p.key}
                  className={`card-tactile p-7 animate-fade-up`}
                  style={{ animationDelay: `${i * 60}ms` }}
                  data-testid={`pillar-${p.key}`}
                >
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: p.bg }}>
                    <Ico className="w-7 h-7" style={{ color: p.color }} />
                  </div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#4A4A4A] mb-1">{p.key.toUpperCase()}.</div>
                  <h3 className="font-serif-display text-2xl text-[#1B4332] mb-2">{t(`${p.key}_title`)}</h3>
                  <p className="text-sm text-[#4A4A4A] leading-relaxed">{t(`${p.key}_desc`)}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-10">
            <Link to="/work">
              <Button variant="outline" className="rounded-full border-[#1B4332] text-[#1B4332]">
                {t("ctaLearn")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="relative rounded-3xl bg-[#1B4332] p-12 md:p-20 overflow-hidden">
          <div className="absolute -right-12 -bottom-12 w-64 h-64 rounded-full bg-[#C95B24]/25 blur-2xl" />
          <div className="relative max-w-2xl">
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#FDFBF7] mb-4">{t("joinTitle")}</h2>
            <p className="text-lg text-[#FDFBF7]/85 mb-8">{t("joinSub")}</p>
            <Link to="/donate">
              <Button className="btn-saffron rounded-full px-8 py-6 text-base" data-testid="cta-donate-btn">
                <Heart className="w-5 h-5 mr-2" /> {t("ctaDonate")}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
